import { useLayoutEffect, useRef } from "react";

// Geometry follows layout; playback follows only a plant's off -> on transition.
export function usePlantConnections({ scope, plantKeys, enabled, selection, boxRef, svgRef, anchorRef, listRef }) {
  const animations = useRef(new Map());
  const previous = useRef(new Map());
  const reduced = useRef(false);

  useLayoutEffect(() => {
    const running = animations.current;
    previous.current = new Map();
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const preference = () => {
      reduced.current = !!media?.matches;
      if (reduced.current) {
        running.forEach(animation => animation.cancel());
        running.clear();
      }
    };
    preference();
    media?.addEventListener?.("change", preference);
    return () => {
      media?.removeEventListener?.("change", preference);
      running.forEach(animation => animation.cancel());
      running.clear();
    };
  }, [scope, enabled]);

  useLayoutEffect(() => {
    const box = boxRef.current, svg = svgRef.current;
    const anchor = anchorRef.current, list = listRef.current;
    if (!enabled || !box || !svg || !anchor || !list) return;
    let frame = null;
    const measure = () => {
      frame = null;
      if (!box.clientWidth || !box.clientHeight) return;
      const b = box.getBoundingClientRect(), a = anchor.getBoundingClientRect();
      const l = list.getBoundingClientRect();
      const paths = new Map([...svg.querySelectorAll(".fan-line")].map(path => [path.dataset.fan, path]));
      // offsetTop/Height ignore each card's entrance and hover transforms.
      const geometry = [...list.querySelectorAll(".werk-card")].map(card => {
        const x1 = a.right - b.left + 4, y1 = a.top - b.top + a.height / 2;
        const x2 = l.left - b.left + card.offsetLeft - 4;
        const y2 = l.top - b.top + card.offsetTop + card.offsetHeight / 2;
        return [paths.get(card.dataset.werk), `M ${x1} ${y1} C ${x1 + (x2 - x1) * .45} ${y1}, ${x2 - (x2 - x1) * .45} ${y2}, ${x2} ${y2}`];
      });
      svg.setAttribute("viewBox", `0 0 ${box.clientWidth} ${box.clientHeight}`);
      for (const [path, d] of geometry) {
        if (path && path.getAttribute("d") !== d) path.setAttribute("d", d);
      }
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    [box, anchor, list, ...list.children].forEach(el => observer?.observe(el));
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [scope, plantKeys, enabled, boxRef, svgRef, anchorRef, listRef]);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!enabled || !svg) return;
    const next = new Map();
    for (const path of svg.querySelectorAll(".fan-line")) {
      const key = path.dataset.fan, on = !!selection?.[key];
      next.set(key, on);
      if (previous.current.get(key) === on) continue;
      animations.current.get(key)?.cancel();
      animations.current.delete(key);
      if (on && !reduced.current && path.animate) {
        const easing = getComputedStyle(svg).getPropertyValue("--ease-out").trim() || "ease-out";
        const animation = path.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 420, easing });
        animations.current.set(key, animation);
        animation.onfinish = () => {
          if (animations.current.get(key) === animation) animations.current.delete(key);
        };
      }
    }
    for (const [key, animation] of animations.current) {
      if (!next.has(key)) { animation.cancel(); animations.current.delete(key); }
    }
    previous.current = next;
  }, [scope, plantKeys, enabled, selection, svgRef]);
}
